import { FC, useState, useCallback } from "react";
import { FieldChange, PersonalDetails, ReferralDetails } from "../../../../types/travel/Policy";
import TextInput from "../../../../components/form-fields/TextInput";
import { FormGroup } from "../../../../components/forms";
import { H2 } from "../../../../components/basics";
import { DateInput } from "../../../../components/form-fields";
import CheckInput from "../../../../components/form-fields/CheckInput";
import { personalDetailsValidations } from "../../../../helpers/travel/validations";
import IdentificationNumberTextInput from "../../../../components/form-fields/IdentificationNumberTextInput";
import ReferralDialog from "../dialog/ReferralDialog";
import { hasTravellersGroupInvalidId } from "../../../../helpers/travel/referral";
import ServerError from "../../../errors/ServerError";
import useForm from "../../../../hooks/useForm";
import { useTranslation } from "react-i18next";
import AdditionalTraveller from "../more-info/AdditionalTraveller";
import { getSpouseFromTravellers } from "../../../../helpers/travel/travellers";
import classNamePresets from "../../../../utils/classNamePresets";
import PolicyFormStepNavigation from "../PolicyFormStepNavigation";
import { trimAndNormalizeSpaces } from "../../../../helpers/shared/formHandlers";

export interface PersonalDetailsProps {
  flowStateSlice: PersonalDetails;
  onFieldChange: FieldChange<PersonalDetails>;
  onBack: () => void;
  onNext: () => void;
  onReferral: (details: ReferralDetails) => Promise<void>;
}

const trimAndReplaceSpace = (
  field: any,
  value: any,
  onFieldChange: any,
  validateField: (name: string, value: unknown, newData?: Record<string, unknown>) => void
) => {
  const newValue = value.replace(/[^a-zA-Z0-9\s]/g, "");
  const trimmedValue = trimAndNormalizeSpaces(newValue);
  onFieldChange(field, trimmedValue);
  validateField(field, trimmedValue, value);
};

const Personal: FC<React.PropsWithChildren<PersonalDetailsProps>> = ({ flowStateSlice, onFieldChange, onBack, onNext, onReferral }) => {
  const [showReferral, setShowReferral] = useState(false);
  const { validateField, validateForm, errors, field } = useForm(flowStateSlice, personalDetailsValidations, onFieldChange);
  const [serverError, setServerError] = useState(false);
  const [showMoreTravellerInfo, setShowMoreTravellerInfo] = useState(false);
  const toggleShowTravellerInfo = useCallback(() => setShowMoreTravellerInfo(v => !v), [setShowMoreTravellerInfo]);
  const [isHandlingReferralConfirm, setIsHandlingReferralConfirm] = useState(false);
  const { t } = useTranslation("pages", { keyPrefix: "travel.newPolicy.steps.personal" });
  const { t: tRequester } = useTranslation("pages", { keyPrefix: "travel.newPolicy.steps.personal.form.label.requester" });
  const { t: tTraveller } = useTranslation("pages", { keyPrefix: "travel.newPolicy.steps.personal.form.label.extraTraveller" });
  const { t: tSupportMailId } = useTranslation("pages", { keyPrefix: "travel.newPolicy.customerSupport" });

  const handleNext = () => {
    hasTravellersGroupInvalidId(flowStateSlice) && setShowReferral(true);
    validateForm(onNext);
  };

  const handleReferralCancel = () => {
    setServerError(false);
    setShowReferral(false);
  };
  const handleReferralConfirm = (details: ReferralDetails) => {
    setIsHandlingReferralConfirm(true);
    setServerError(false);
    onReferral(details)
      .then(() => {
        setIsHandlingReferralConfirm(false);
        setShowReferral(false);
      })
      .catch(() => {
        setIsHandlingReferralConfirm(false);
        setServerError(true);
      });
  };

  const { requester, travellers, childTravellers } = flowStateSlice;
  const totalTravellers = [...travellers, ...childTravellers];
  let childIndex = 0;

  return (
    <>
      <div className="container-flow flex-grow">
        <H2 className={classNamePresets.mainHeading}>{t("title")}</H2>
        {serverError && <ServerError supportEmailId={tSupportMailId("emailId")} />}
        <FormGroup title={t("form.groupTitle.requester")} subtitle={t("form.groupTitle.requester_sub")} hasVerticalFieldsOrientation={true}>
          <TextInput
            label={tRequester("fullName")}
            value={requester.fullName}
            {...field.requester.fullName}
            onBlur={event => trimAndReplaceSpace("requester.fullName", event.target.value, onFieldChange, validateField)}
          />
          <TextInput label={tRequester("phoneNumber")} value={requester.phoneNumber} {...field.requester.phoneNumber} />
          <TextInput label={tRequester("email")} value={requester.email} {...field.requester.email} />
          <IdentificationNumberTextInput
            label={tRequester("identificationNumber")}
            identificationNumber={requester.identificationNumber}
            identificationNumberStatus={requester.identificationNumberStatus}
            onValueChange={field.requester.identificationNumber.onValueChange}
            setIdentificationNumberStatus={field.requester.identificationNumberStatus.onValueChange}
            validate={v => validateField(`requester.identificationNumber`, v)}
            error={field.requester.identificationNumber.error}
            isReferral={() => setShowReferral(true)}
            setServerError={setServerError}
          />
          <DateInput
            label={tRequester("dateOfBirth")}
            name="date-of-birth"
            value={requester.dateOfBirth}
            onValueChange={v => {
              field.requester.dateOfBirth.onValueChange(v);
              validateField(`requester.${String("dateOfBirth")}`, v);
            }}
            onBlur={() => validateField("requester.dateOfBirth", requester.dateOfBirth)}
            error={errors[`requester.dateOfBirth`]}
          />
        </FormGroup>
        <FormGroup title={t("form.groupTitle.requesterIsTravelling")} hasVerticalFieldsOrientation={true}>
          <CheckInput
            checked={flowStateSlice.requestingPersonIsTravelingPerson}
            onValueChange={v => onFieldChange("requestingPersonIsTravelingPerson", v)}
            label="Yes"
            name="requestingPersonIsTravelingPerson"
            disabled={travellers.length === 0}
          />
        </FormGroup>
        {totalTravellers.length > 0 &&
          totalTravellers.map((traveller, index) => {
            const shouldTravellerBeSpouse = getSpouseFromTravellers(flowStateSlice.isTravellerTypeGroup, travellers.length, index);
            const shouldDisabled = flowStateSlice.requestingPersonIsTravelingPerson ? index === 0 : false;
            let fieldTraveller = field.travellers[index];
            let indexTraveller = `travellers.${index}`;
            let flowTraveller = flowStateSlice.travellers[index];
            if (traveller.isChild) {
              fieldTraveller = field.childTravellers[childIndex];
              indexTraveller = `childTravellers.${childIndex}`;
              flowTraveller = flowStateSlice.childTravellers[childIndex];
              childIndex++;
            }
            return (
              <FormGroup
                key={`${index}-${totalTravellers.length}`}
                title={
                  traveller.isChild
                    ? t("form.groupTitle.childTraveller", { number: childIndex })
                    : t("form.groupTitle.adultTraveller", { number: index + 1 })
                }
                hasVerticalFieldsOrientation={true}
                onMoreInfo={shouldTravellerBeSpouse ? toggleShowTravellerInfo : undefined}
              >
                <TextInput
                  label={tTraveller("fullName")}
                  {...fieldTraveller.fullName}
                  value={traveller.fullName}
                  onBlur={event => trimAndReplaceSpace(`${indexTraveller}.fullName`, event.target.value, onFieldChange, validateField)}
                  disabled={shouldDisabled}
                />
                <IdentificationNumberTextInput
                  label={tTraveller("identificationNumber")}
                  isReferral={() => setShowReferral(true)}
                  identificationNumber={traveller.identificationNumber}
                  identificationNumberStatus={fieldTraveller.identificationNumber.error ? "unknown" : traveller.identificationNumberStatus}
                  onValueChange={fieldTraveller.identificationNumber.onValueChange}
                  setIdentificationNumberStatus={fieldTraveller.identificationNumberStatus.onValueChange}
                  validate={v => validateField(`${indexTraveller}.identificationNumber`, v)}
                  setServerError={setServerError}
                  error={fieldTraveller.identificationNumber.error}
                  disabled={shouldDisabled}
                />
                <DateInput
                  label={tTraveller("dateOfBirth")}
                  value={flowTraveller.dateOfBirth}
                  {...fieldTraveller.dateOfBirth}
                  name="date-of-birth"
                  onValueChange={v => {
                    fieldTraveller.dateOfBirth.onValueChange(v);
                    validateField(`${indexTraveller}.dateOfBirth`, v);
                  }}
                  error={shouldDisabled ? undefined : errors[`${indexTraveller}.dateOfBirth`]}
                  disabled={shouldDisabled}
                />
              </FormGroup>
            );
          })}
      </div>

      {showReferral && (
        <ReferralDialog
          onConfirm={details => handleReferralConfirm(details)}
          onCancel={handleReferralCancel}
          serverError={serverError}
          isLoading={isHandlingReferralConfirm}
        />
      )}
      <AdditionalTraveller show={showMoreTravellerInfo} onClose={toggleShowTravellerInfo} />
      <PolicyFormStepNavigation onBack={onBack} onNext={handleNext} />
    </>
  );
};

export default Personal;
